import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  Button,
  renderInput,
  Accordion,
  Divider,
  getIcon,
} from "@dishopsaas/design-system";
import { displayPriceFormat, getColors } from "../../../utils";
import { addPromotion } from "../../../components/Order/Order.services";
import { getBillingFlags } from "../PaymentSummary.services";
import { getTitleFromOffer } from "../../../components/ThresholdMarkertingProgress/ThresholdMarketingProgress.service";
import {
  PRIMARY,
  TERTIARY,
  PROMOTION_PERCENTAGE,
  LOYALTY,
} from "../../../constants";
import { checkOrder } from "../PaymentSummary.services";
import {
  BillingRowProps,
  BillingProps,
} from "../../../1_types/paymentSummaryTypes";
import LoyaltyBanner from "../../../components/LoyaltyBanner/LoyaltyBanner";
const rowSx = { mb: 3, display: "flex", justifyContent: "space-between" };
export const CardBox: React.FC<{ children: React.ReactNode; sx?: any }> = ({
  children,
  sx = {},
}) => (
  <Box
    sx={{
      color: getColors().text,
      borderRadius: 1,
      bgcolor: getColors().bgwhite,
      border: getColors().border,
      fontFamily: "PP Right Grotesk Casual Variable",
      ...sx,
    }}
  >
    {children}
  </Box>
);

export const BillingRow: React.FC<BillingRowProps> = ({
  label,
  value,
  isNegative = false,
  isMinBasketPrice = false,
  isFee = false,
}) => {
  if (value === undefined || value === null) return null;
  const formattedValue = displayPriceFormat(value, !isFee);
  const labelSx = {
    textTransform: "none",
    color: isMinBasketPrice ? "red" : "inherit",
    textDecoration: isMinBasketPrice ? "line-through" : "none",
  };
  return (
    <Box sx={rowSx}>
      <Typography variant="body1" sx={labelSx}>
        {label}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          ...labelSx,
          fontWeight: 600,
          color: isMinBasketPrice
            ? "error.main"
            : isNegative
              ? "success.main"
              : "inherit",
        }}
      >
        {isNegative ? "-" : ""}
        {isNegative ? value : formattedValue}
      </Typography>
    </Box>
  );
};

const Billing: React.FC<BillingProps> = ({
  totalProductPriceOrder,
  totalPriceOrder,
  promotion,
  managementFee,
  expanded,
  setExpanded,
  paymentType,
  card,
  commande,
  selectTime,
  setLoading,
  isPhoneIncorrect,
  setChoosePayment,
  phoneObj,
  setDateState,
  stripe,
  loading,
  thresholdOffer,
  marketingCampaignPromoAmount,
  freeDelivery,
  orderType,
  address,
}) => {
  const [promoCode, setPromoCode] = useState("");
  const [loadingPromotion, setLoadingPromotion] = useState<boolean>(false);
  const [promoMessage, setPromoMessage] = useState("");
  const { isDeliveryFree, isClickAndCollectPromo, shouldShowDeliveryFee } =
    getBillingFlags({ promotion, freeDelivery, orderType });
  const isMinBasketPrice = totalProductPriceOrder < promotion?.minBasketPrice;
  const isLoyalty = promotion?.promoCode === LOYALTY;
  const textPromo = isLoyalty
    ? `Fidélité ${promotion?.name || ""}`
    : `Promotion ${promotion?.promoCode}`;
  const userServicePrice = address?.deliveryPrice ?? 0;

  const renderPromotion = () => {
    if (!promotion) return null;

    let displayValue = "";
    if (
      typeof promotion.type === "string" &&
      promotion.type.includes(PROMOTION_PERCENTAGE)
    ) {
      displayValue = promotion.type;
    } else if (typeof promotion.type === "number") {
      displayValue = displayPriceFormat(promotion.type);
    }

    return (
      <BillingRow
        label={textPromo}
        value={displayValue}
        isNegative
        isMinBasketPrice={isMinBasketPrice}
      />
    );
  };

  const applyPromoCode = async () => {
    await addPromotion(
      setLoadingPromotion,
      setPromoMessage,
      promoCode,
      totalProductPriceOrder,
    );
  };
  const renderLoyaltyBanner = () => {
    if (!isLoyalty) return null;
    return (
      <Box sx={{ pb: 4 }}>
        <LoyaltyBanner commande={commande} />
      </Box>
    );
  };
  const { border } = getColors();
  const showDiscountPrice = totalProductPriceOrder > totalPriceOrder;
  return (
    <CardBox>
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ pb: 4 }}>
          Récapitulatif de la commande
        </Typography>
        <BillingRow label="Sous-Total" value={totalProductPriceOrder} />
        {promotion &&
          !isClickAndCollectPromo &&
          !isDeliveryFree &&
          renderPromotion()}
        {!!marketingCampaignPromoAmount && (
          <BillingRow
            label={
              commande
                ? "Campagne Marketing"
                : getTitleFromOffer(thresholdOffer)
            }
            isNegative
            value={displayPriceFormat(marketingCampaignPromoAmount)}
          />
        )}
        {(!!shouldShowDeliveryFee || !!managementFee) && (
          <>
            {!!shouldShowDeliveryFee && (
              <BillingRow
                label="Frais de livraison"
                value={userServicePrice}
                isFee={true}
              />
            )}
            {!!managementFee && (
              <BillingRow
                label="Frais et services"
                value={managementFee}
                isFee={true}
              />
            )}
          </>
        )}
        {isDeliveryFree && userServicePrice && (
          <BillingRow label=" Livraison offert" value={-userServicePrice} />
        )}
        {isClickAndCollectPromo && renderPromotion()}
      </Box>
      <Box sx={{ px: 1, pb: 1 }}>
        {expanded && <Divider sx={{ borderBottomColor: border }} />}
        <Accordion
          title="Code Promo"
          leftIcon="Gift"
          expanded={expanded}
          onChange={setExpanded}
        >
          {renderInput({
            disabled: loadingPromotion,
            value: promoCode,
            variant: "outlined",
            onChange: (e) => setPromoCode(e.target.value),
            hasTag: true,
            helperText: promoMessage ? (
              <Box display="flex" alignItems="center" gap={2} sx={{ mt: 1 }}>
                {getIcon("info", { size: 14 })}
                <Typography variant="caption"> {promoMessage}</Typography>
              </Box>
            ) : null,
            rightIcon: (
              <Button
                colorType={TERTIARY}
                label="Valider"
                onClick={applyPromoCode}
                height={30}
              />
            ),
          })}
        </Accordion>
      </Box>
      <Divider sx={{ borderBottomColor: border }} />
      <Box
        sx={{
          pt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          px: 3,
          pb: 3,
        }}
      >
        {renderLoyaltyBanner()}
        <Box display="flex" justifyContent="space-between" pb={2}>
          <Typography variant="h5">TOTAL</Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {showDiscountPrice && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  textDecoration: "line-through",
                }}
              >
                {displayPriceFormat(totalProductPriceOrder)}
              </Typography>
            )}

            <Typography
              variant="body2"
              sx={{
                fontWeight: "bold",
                color: showDiscountPrice ? "success.main" : null,
              }}
            >
              {displayPriceFormat(totalPriceOrder)}
            </Typography>
          </Box>
        </Box>
        <Button
          loading={loading}
          colorType={PRIMARY}
          sx={{ width: "100%" }}
          label="COMMANDER . MAINTENANT"
          onClick={async () => {
            await checkOrder({
              paymentType,
              card,
              commande,
              selectTime,
              setLoading,
              isPhoneIncorrect,
              setChoosePayment,
              phoneObj,
              setDateState,
              stripe,
            });
          }}
        />
      </Box>
    </CardBox>
  );
};

export default Billing;
